import { isTrustCircleGroup } from 'app/types/group-base';
import type { ReactNode } from 'react';
import { Pressable } from 'react-native';
import { IconLock, IconChevronRight, IconHeartHandshake } from '@tabler/icons-react-native';
import { useTranslation } from 'react-i18next';
import { Clubs } from 'app/design/icons/clubs';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Switch } from 'app/design/primitives/switch';
import { Text } from 'app/design/primitives/text';
import { getAddressIcon } from 'app/screens/profile/my-clubs';
import { createTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const theme = createTheme();

export interface ListingVisibility {
  addressIds: string[];
  groupIds: string[];
}

export interface ListingVisibilitySelectorProps {
  addresses: { id: string; name?: string | null; icon?: number | null }[];
  groups: {
    id: string;
    name: string;
    picture?: string | null;
    ownerId?: string | null;
    type?: string;
    subtype?: string;
  }[];
  value: ListingVisibility;
  onChange: (value: ListingVisibility) => void;
  disabled?: boolean;
  showTitle?: boolean;
  showGroupsHeading?: boolean;
  privateHint?: string;
}

function VisibilityRow({
  icon,
  iconBackgroundColor = 'surface.secondary-neutral',
  label,
  description,
  selected,
  disabled,
  last,
  onChange,
}: {
  icon: ReactNode;
  iconBackgroundColor?: keyof typeof theme.colors;
  label: string;
  description?: string;
  selected: boolean;
  disabled: boolean;
  last: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <Row
      alignItems="center"
      gap="spacing.general.x3"
      minHeight={52}
      paddingVertical="spacing.general.x2"
      borderBottomWidth={last ? 0 : 1}
      borderColor="border.secondary"
    >
      <Row flex={1} alignItems="center" gap="spacing.general.x2" opacity={selected ? 1 : 0.6}>
        <Column
          width={32}
          height={32}
          alignItems="center"
          justifyContent="center"
          backgroundColor={iconBackgroundColor}
          borderRadius="radius.sm"
        >
          {icon}
        </Column>
        <Column flex={1}>
          <Text variant="bodyBase" fontWeight="600">
            {label}
          </Text>
          {description ? (
            <Text color="text.secondary" variant="bodySmall" fontSize={12}>
              {description}
            </Text>
          ) : null}
        </Column>
      </Row>
      <Switch accessibilityLabel={label} disabled={disabled} value={selected} onValueChange={onChange} />
    </Row>
  );
}

export function ListingVisibilitySelector({
  addresses,
  groups,
  value,
  onChange,
  disabled = false,
  showTitle = true,
  showGroupsHeading = true,
  privateHint,
}: ListingVisibilitySelectorProps) {
  const { t } = useTranslation('new');
  const selectedAddressIds = new Set(value.addressIds);
  const selectedGroupIds = new Set(value.groupIds);
  const isPrivate = value.addressIds.length + value.groupIds.length === 0;
  const toggle = (ids: string[], id: string, on: boolean) =>
    on ? [...new Set([...ids, id])] : ids.filter((selectedId) => selectedId !== id);
  return (
    <Column gap="spacing.general.x2">
      {showTitle ? (
        <Text variant="bodyBase" fontWeight="700">
          {t('locationSection.title')}
        </Text>
      ) : null}
      <Column
        backgroundColor="surface.default"
        borderColor="border.secondary"
        borderWidth={1}
        borderRadius="radius.lg"
        padding="spacing.general.x3"
        gap="spacing.general.x3"
      >
        {addresses.length ? (
          <Column>
            <Text variant="bodySmall" color="text.secondary" fontWeight="600">
              {t('locationSection.addressesTitle')}
            </Text>
            {addresses.map((address, index) => {
              const home = !address.name || address.name === 'Domicile' || address.name === 'Home';
              return (
                <VisibilityRow
                  key={address.id}
                  icon={getAddressIcon({ icon: address.icon ?? null }, 22, theme.colors['text.primary'])}
                  label={
                    home
                      ? t('locationSection.addressFieldTitle')
                      : t('locationSection.groupFieldTitle', { clubName: address.name })
                  }
                  description={
                    home
                      ? t('locationSection.addressFieldSubtitle')
                      : t('locationSection.addressFieldSubtitleCustom', { clubName: address.name })
                  }
                  selected={selectedAddressIds.has(address.id)}
                  disabled={disabled}
                  last={index === addresses.length - 1}
                  onChange={(on) => onChange({ ...value, addressIds: toggle(value.addressIds, address.id, on) })}
                />
              );
            })}
          </Column>
        ) : null}
        {[
          {
            key: 'trust-circle',
            title: t('trust.circleTitle', { ns: 'neighbour-profile' }),
            groups: groups.filter(isTrustCircleGroup),
          },
          {
            key: 'partner-clubs',
            title: t('locationSection.clubsTitle'),
            groups: groups.filter((group) => !isTrustCircleGroup(group)),
          },
        ].map((section) =>
          section.groups.length ? (
            <Column key={section.key}>
              {showGroupsHeading ? (
                <Text variant="bodySmall" color="text.secondary" fontWeight="600">
                  {section.title}
                </Text>
              ) : null}
              {section.groups.map((group, index) => (
                <VisibilityRow
                  key={group.id}
                  iconBackgroundColor={isTrustCircleGroup(group) ? 'surface.brand.secondary-4.subtle' : undefined}
                  icon={
                    isTrustCircleGroup(group) ? (
                      <IconHeartHandshake
                        color={theme.colors['text.primary']}
                        size={22}
                        strokeWidth={ICON_STROKE_WIDTH}
                      />
                    ) : group.picture ? (
                      <PCImage
                        source={{ uri: group.picture }}
                        width={28}
                        height={28}
                        style={{ borderRadius: theme.borderRadii['radius.sm'] }}
                      />
                    ) : (
                      <Clubs color={theme.colors['text.primary']} width={22} height={22} />
                    )
                  }
                  label={isTrustCircleGroup(group) ? t('trust.circleTitle', { ns: 'neighbour-profile' }) : group.name}
                  selected={selectedGroupIds.has(group.id)}
                  disabled={disabled}
                  last={index === section.groups.length - 1}
                  onChange={(on) => onChange({ ...value, groupIds: toggle(value.groupIds, group.id, on) })}
                />
              ))}
            </Column>
          ) : null,
        )}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t(isPrivate ? 'locationSection.shareWithAll' : 'locationSection.makePrivate')}
          accessibilityState={{ disabled: disabled || (isPrivate && addresses.length + groups.length === 0) }}
          disabled={disabled || (isPrivate && addresses.length + groups.length === 0)}
          onPress={() =>
            onChange(
              isPrivate
                ? { addressIds: addresses.map(({ id }) => id), groupIds: groups.map(({ id }) => id) }
                : { addressIds: [], groupIds: [] },
            )
          }
          style={({ pressed }) => ({
            minHeight: 48,
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            padding: 12,
            borderRadius: theme.borderRadii['radius.sm'],
            backgroundColor: theme.colors[isPrivate ? 'surface.brand.subtle' : 'surface.secondary-neutral'],
            opacity: disabled ? 0.5 : pressed ? 0.7 : 1,
          })}
        >
          <IconLock size={20} strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
          <Column flex={1} minWidth={120} gap="spacing.general.x0,5" accessibilityLiveRegion="polite">
            <Text variant="bodySmall" fontWeight="600">
              {t(isPrivate ? 'locationSection.privateVisibility' : 'locationSection.makePrivate')}
            </Text>
            {isPrivate ? (
              <Text color="text.secondary" variant="bodySmall">
                {privateHint ?? t('locationSection.invisible')}
              </Text>
            ) : null}
          </Column>
          {isPrivate ? (
            <Text variant="bodySmall" fontWeight="600" textDecorationLine="underline">
              {t('locationSection.shareWithAll')}
            </Text>
          ) : (
            <IconChevronRight size={18} strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.secondary']} />
          )}
        </Pressable>
      </Column>
    </Column>
  );
}
